// =======================
// sear/src/paginas/analise/processos/processosAnalista.js
// =======================

import React, { useState, useEffect, useContext, useRef } from "react";
import {
  CssBaseline,
  Container,
  List,
  ListItem,
  ListItemText,
  Collapse,
  TextField,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  Badge,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  Description as DescriptionIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  Category as CategoryIcon,
  ChevronRight as ChevronRightIcon,
  FilterList as FilterListIcon,
  Sort as SortIcon,
  SwapHoriz as SwapHorizIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { styled } from "@mui/material/styles";
import { api } from "@api/api";
import { CondicaoContext } from "../../../contexto";
import { ajustarTamanhoFonteAnalista } from "../../../estilos/global";
import TransicaoPagina from "../../../componentes/comuns/TransicaoPagina";

// Componentes estilizados
const StyledProcessItem = styled(ListItem)(({ theme }) => ({
  transition: "all 0.3s ease",
  borderRadius: theme.shape.borderRadius,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    transform: "translateX(5px)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    backgroundColor: "transparent",
    transition: "background-color 0.3s ease",
  },
  "&:hover::before": {
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledModalityItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
}));

const StyledSchoolItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));

const TransitionContainer = styled(motion.div)({
  position: "relative",
  width: "100%",
  minHeight: "400px",

  "@media (min-width: 900px)": {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minHeight: 0,

    overflowY: "auto",
    overflowX: "hidden",
    scrollbarGutter: "stable",
    overscrollBehavior: "contain",

    "& > div": {
      display: "flex",
      flexDirection: "column",
      flex: "1 0 auto",
    },

    "& > div > .MuiPaper-root": {
      flex: "1 0 auto",
    },
  },
});

const ModeSwitchButton = styled(Button)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    zIndex: -1,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover::before": {
    opacity: 0.1,
  },
}));

const EmptyProcessState = ({ onRetry, mode }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "40px 20px",
      width: "100%",
      minHeight: "400px",
    }}
  >
    <motion.div
      animate={{
        scale: [1, 1.05, 1],
        rotate: [0, -3, 3, 0],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        repeatType: "reverse",
      }}
    >
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          backgroundColor:
            mode === "open"
              ? "rgba(25, 118, 210, 0.1)"
              : "rgba(156, 39, 176, 0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
          boxShadow:
            mode === "open"
              ? "0 8px 24px rgba(25, 118, 210, 0.1)"
              : "0 8px 24px rgba(156, 39, 176, 0.1)",
        }}
      >
        <DescriptionIcon
          sx={{
            fontSize: 60,
            color: mode === "open" ? "primary.main" : "secondary.main",
          }}
        />
      </Box>
    </motion.div>

    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        mb: 1.5,
        background:
          mode === "open"
            ? "linear-gradient(90deg, #1976d2, #2196f3)"
            : "linear-gradient(90deg, #9c27b0, #ba68c8)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }}
    >
      {mode === "open"
        ? "Nenhum processo aberto"
        : "Nenhum processo finalizado"}
    </Typography>

    <Typography
      variant="body1"
      sx={{
        color: "text.secondary",
        mb: 4,
        maxWidth: "450px",
      }}
    >
      {mode === "open"
        ? "Não há processos abertos no sistema no momento."
        : "Não há processos finalizados no sistema no momento."}
    </Typography>

    <Button
      variant="contained"
      onClick={onRetry}
      startIcon={<RefreshIcon />}
      color={mode === "open" ? "primary" : "secondary"}
      sx={{
        borderRadius: "20px",
        px: 4,
        py: 1,
        fontWeight: "bold",
        boxShadow:
          mode === "open"
            ? "0 4px 12px rgba(25, 118, 210, 0.2)"
            : "0 4px 12px rgba(156, 39, 176, 0.2)",
      }}
    >
      Recarregar
    </Button>
  </motion.div>
);

const ProcessItem = ({ process, school, onClick }) => {
  // Função auxiliar para determinar a cor do chip
  const getTagColor = (dias) => {
    if (dias <= 20) return "success"; // verde
    if (dias <= 40) return "warning"; // amarelo
    if (dias <= 60) return "error"; // vermelho
    return "default"; // acima de 60 dias
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      exit={{ opacity: 0, x: -10 }}
    >
      <StyledProcessItem button onClick={onClick}>
        <ListItemText
          primary={process.processo_nome}
          secondary={
            process.data_criacao
              ? new Date(process.data_criacao).toLocaleDateString()
              : ""
          }
          sx={{
            "& .MuiListItemText-primary": {
              fontWeight: 500,
              color: "text.primary",
            },
            "& .MuiListItemText-secondary": {
              fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
              color: "text.secondary",
            },
          }}
        />

        {process.dias_em_aberto !== undefined && (
          <Chip
            label={`${process.dias_em_aberto} dia(s)`}
            color={getTagColor(process.dias_em_aberto)}
            size="small"
            sx={{ fontWeight: "bold", ml: 2 }}
          />
        )}

        <ChevronRightIcon color="action" />
      </StyledProcessItem>
    </motion.div>
  );
};

const ModalityItem = ({ modality, school, expanded, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: -5 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.2 }}
    exit={{ opacity: 0, y: -5 }}
  >
    <StyledModalityItem button onClick={onClick}>
      <Avatar sx={{ bgcolor: "primary.main", mr: 2, width: 32, height: 32 }}>
        <CategoryIcon fontSize="small" />
      </Avatar>

      <ListItemText
        primary={modality.name}
        secondary={`${modality.processes.length} processo(s)`}
      />

      {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </StyledModalityItem>
  </motion.div>
);

const formatAnalystShortName = (fullName = "") => {
  const nameParts = fullName
    .trim()
    .split(/\s+/)
    .filter((part) => /[A-Za-zÀ-ÿ]/.test(part));

  if (nameParts.length === 0) return "";
  if (nameParts.length === 1) return nameParts[0];

  return `${nameParts[0]} ${nameParts[1].charAt(0).toUpperCase()}.`;
};

const SchoolItem = ({ school, expanded, onClick, mode }) => {
  const processes = school.modalities.flatMap((modality) => modality.processes);

  const analystNames = [
    ...new Set(
      processes.map((process) => process.nome_analista).filter(Boolean),
    ),
  ];

  const hasResponsibleAnalyst = analystNames.length > 0;

  const assignedToLoggedAnalyst = processes.some(
    (process) => process.atribuido_ao_usuario_logado === true,
  );

  const shortAnalystNames = analystNames.map(formatAnalystShortName);

  const responsibleLabel = assignedToLoggedAnalyst
    ? "Atribuída a você"
    : shortAnalystNames.length === 0
      ? "Sem responsável"
      : shortAnalystNames.length === 1
        ? `Responsável: ${shortAnalystNames[0]}`
        : `Responsáveis: ${shortAnalystNames.join(", ")}`;

  return (
    <motion.div layout>
      <StyledSchoolItem
        button
        onClick={onClick}
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: "4px",
            backgroundColor: assignedToLoggedAnalyst
              ? "success.main"
              : "transparent",
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: mode === "open" ? "primary.dark" : "secondary.dark",
            mr: 2,
          }}
        >
          <SchoolIcon />
        </Avatar>

        <ListItemText
          primary={school.name}
          sx={{
            "& .MuiListItemText-primary": {
              fontWeight: "bold",
            },
          }}
        />

        <Tooltip title={responsibleLabel} arrow>
          <Box
            sx={{
              position: "relative",
              width: 36,
              height: 36,
              borderRadius: "50%",
              backgroundColor: hasResponsibleAnalyst
                ? mode === "open"
                  ? "primary.dark"
                  : "secondary.dark"
                : "rgba(255, 255, 255, 0.95)",
              color: hasResponsibleAnalyst ? "white" : "error.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ml: "auto",
              mr: 2,
              flexShrink: 0,
            }}
          >
            <PersonIcon fontSize="small" />

            {assignedToLoggedAnalyst && (
              <Box
                sx={{
                  position: "absolute",
                  right: -4,
                  bottom: -4,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "success.main",
                  color: "white",
                  border: "2px solid white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: ajustarTamanhoFonteAnalista("0.7rem"),
                  fontWeight: "bold",
                }}
              >
                ✓
              </Box>
            )}
          </Box>
        </Tooltip>

        <Badge
          badgeContent={school.modalities.reduce(
            (acc, curr) => acc + curr.processes.length,
            0,
          )}
          color={mode === "open" ? "primary" : "secondary"}
          sx={{ mr: 2 }}
        />

        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </StyledSchoolItem>
    </motion.div>
  );
};

export default function PaginaProcessos() {
  const { userId } = useContext(CondicaoContext);

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedSchool, setExpandedSchool] = useState(null);
  const [expandedModality, setExpandedModality] = useState({});
  const containerRef = useRef(null);

  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modoAberto, setModoAberto] = useState(true);

  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  const [isTransitioning, setIsTransitioning] = useState(false);

  const [assignmentFilters, setAssignmentFilters] = useState({
    open: "all",
    finished: "all",
  });

  const currentModeKey = modoAberto ? "open" : "finished";
  const assignmentFilter = assignmentFilters[currentModeKey];

  useEffect(() => {
    const defaultFilters = {
      open: "all",
      finished: "all",
    };

    if (!userId) {
      setAssignmentFilters(defaultFilters);
      return;
    }

    const storageKey = `processosAnalistaFilters_${userId}`;

    try {
      const savedFilters = localStorage.getItem(storageKey);

      if (!savedFilters) {
        setAssignmentFilters(defaultFilters);
        return;
      }

      const parsedFilters = JSON.parse(savedFilters);

      const validOpenFilters = ["all", "unassigned", "mine", "others"];

      const validFinishedFilters = ["all", "mine", "others"];

      setAssignmentFilters({
        open: validOpenFilters.includes(parsedFilters.open)
          ? parsedFilters.open
          : "all",
        finished: validFinishedFilters.includes(parsedFilters.finished)
          ? parsedFilters.finished
          : "all",
      });
    } catch (error) {
      console.error("Erro ao recuperar filtros salvos:", error);
      setAssignmentFilters(defaultFilters);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    const lastSearchKey = `processosAnalistaLastSearch_${userId}`;

    try {
      const savedSearch = sessionStorage.getItem(lastSearchKey);

      if (!savedSearch) return;

      const parsedSearch = JSON.parse(savedSearch);

      if (typeof parsedSearch.searchTerm === "string") {
        setSearchTerm(parsedSearch.searchTerm);
      }

      if (
        typeof parsedSearch.expandedSchool === "string" ||
        parsedSearch.expandedSchool === null
      ) {
        setExpandedSchool(parsedSearch.expandedSchool);
      }

      if (
        parsedSearch.expandedModality &&
        typeof parsedSearch.expandedModality === "object"
      ) {
        setExpandedModality(parsedSearch.expandedModality);
      }
    } catch (error) {
      console.error("Erro ao recuperar última busca:", error);
    }
  }, [userId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setExpandedSchool(null);
        setExpandedModality({});

        saveLastSearch({
          expandedSchool: null,
          expandedModality: {},
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const saveLastSearch = (overrides = {}) => {
    if (!userId || !modoAberto) return;

    const lastSearchKey = `processosAnalistaLastSearch_${userId}`;

    const dataToSave = {
      searchTerm,
      expandedSchool,
      expandedModality,
      ...overrides,
    };

    try {
      sessionStorage.setItem(lastSearchKey, JSON.stringify(dataToSave));
    } catch (error) {
      console.error("Erro ao salvar última busca:", error);
    }
  };

  const handleAssignmentFilterChange = (filter) => {
    const updatedFilters = {
      ...assignmentFilters,
      [currentModeKey]: filter,
    };

    setAssignmentFilters(updatedFilters);

    if (userId) {
      try {
        localStorage.setItem(
          `processosAnalistaFilters_${userId}`,
          JSON.stringify(updatedFilters),
        );
      } catch (error) {
        console.error("Erro ao salvar filtros:", error);
      }
    }

    setExpandedSchool(null);
    setExpandedModality({});
  };

  const navigate = useNavigate();

  const fetchProcesses = () => {
    setLoading(true);
    setError(null);

    const endpoint = modoAberto ? api.allProcAna() : api.AllProcFini();

    fetch(endpoint, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) {
          throw new Error("Resposta inválida da API");
        }

        const groupedData = {};

        data.forEach(
          ({
            escola_mantenedora,
            modalidade,
            processo_nome,
            process_id,
            id_analista,
            nome_analista,
            atribuido_ao_usuario_logado,
            data_criacao,
            dias_em_aberto,
          }) => {
            if (!groupedData[escola_mantenedora]) {
              groupedData[escola_mantenedora] = {
                name: escola_mantenedora,
                modalities: {},
              };
            }

            const modalityKey = modalidade || "Todas escolas mantidas";

            if (!groupedData[escola_mantenedora].modalities[modalityKey]) {
              groupedData[escola_mantenedora].modalities[modalityKey] = [];
            }

            groupedData[escola_mantenedora].modalities[modalityKey].push({
              process_id,
              processo_nome,
              id_analista,
              nome_analista,
              atribuido_ao_usuario_logado,
              data_criacao,
              dias_em_aberto,
            });
          },
        );

        const formattedSchools = Object.values(groupedData).map((school) => ({
          name: school.name,
          modalities: Object.entries(school.modalities).map(
            ([modality, processes]) => ({
              name: modality,
              processes: processes,
            }),
          ),
        }));

        setSchools(formattedSchools);
      })
      .catch((error) => {
        console.error("Erro ao buscar processos:", error);
        setError(error.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProcesses();
  }, [modoAberto]);

  const handleToggleMode = () => {
    setIsTransitioning(true);

    setTimeout(() => {
      setModoAberto((prev) => !prev);
      setExpandedSchool(null);
      setExpandedModality({});
      setIsTransitioning(false);
    }, 300);
  };

  const handleExpandClick = (school) => {
    const newExpandedSchool = expandedSchool === school ? null : school;

    setExpandedSchool(expandedSchool === school ? null : school);

    saveLastSearch({
      expandedSchool: newExpandedSchool,
    });
  };

  const handleModalityClick = (schoolName, modalityName) => {
    setExpandedModality((prevState) => {
      const newExpandedModality = {
        ...prevState,
        [schoolName]:
          prevState[schoolName] === modalityName ? null : modalityName,
      };

      saveLastSearch({
        expandedModality: newExpandedModality,
      });

      return newExpandedModality;
    });
  };

  const handleProcessClick = (school, process) => {
    navigate(
      `/processo-detalhado/${encodeURIComponent(
        school.name,
      )}/${process.process_id}`,
    );
  };

  const handleSort = (key) => {
    let direction = "asc";

    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({
      key,
      direction,
    });
  };

  const sortedSchools = [...schools].sort((a, b) => {
    if (sortConfig.key === "name") {
      return sortConfig.direction === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    } else if (sortConfig.key === "count") {
      const countA = a.modalities.reduce(
        (acc, curr) => acc + curr.processes.length,
        0,
      );

      const countB = b.modalities.reduce(
        (acc, curr) => acc + curr.processes.length,
        0,
      );

      return sortConfig.direction === "asc" ? countA - countB : countB - countA;
    }

    return 0;
  });

  const schoolMatchesAssignmentFilter = (school) => {
    const processes = school.modalities.flatMap(
      (modality) => modality.processes,
    );

    const hasAssignedAnalyst = processes.some(
      (process) =>
        process.id_analista !== null && process.id_analista !== undefined,
    );

    const assignedToLoggedAnalyst = processes.some(
      (process) => process.atribuido_ao_usuario_logado === true,
    );

    if (assignmentFilter === "unassigned") {
      return !hasAssignedAnalyst;
    }

    if (assignmentFilter === "mine") {
      return assignedToLoggedAnalyst;
    }

    if (assignmentFilter === "others") {
      return hasAssignedAnalyst && !assignedToLoggedAnalyst;
    }

    return true;
  };

  const filteredSchools = sortedSchools.filter((school) => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const matchesSearch =
      normalizedSearch === "" ||
      school.name?.toLowerCase().includes(normalizedSearch) ||
      school.modalities.some(
        (modality) =>
          modality.name.toLowerCase().includes(normalizedSearch) ||
          modality.processes.some(
            (process) =>
              process.processo_nome.toLowerCase().includes(normalizedSearch) ||
              process.nome_analista?.toLowerCase().includes(normalizedSearch),
          ),
      );

    return schoolMatchesAssignmentFilter(school) && matchesSearch;
  });

  const assignmentFilterOptions = [
    {
      value: "all",
      label: "Todas",
    },
    ...(modoAberto
      ? [
          {
            value: "unassigned",
            label: "Sem atribuição",
          },
        ]
      : []),
    {
      value: "mine",
      label: "Atribuídas a mim",
    },
    {
      value: "others",
      label: "Atribuídas a outros",
    },
  ];

  return (
    <TransicaoPagina variante="navegacao">
      <CssBaseline />

      <Container
        maxWidth={false}
        disableGutters
        sx={{
          mt: { xs: 4, md: 0 },
          mb: { xs: 6, md: 0 },

          display: { md: "flex" },
          flexDirection: "column",
          flex: { md: "1 1 auto" },
          minHeight: { md: 0 },

          "@media (min-width: 900px)": {
            "& > :nth-child(-n+2)": {
              flexShrink: 0,
            },
          },
        }}
      >
        <motion.div layout>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  background: modoAberto
                    ? "linear-gradient(90deg, #1976d2, #2196f3)"
                    : "linear-gradient(90deg, #9c27b0, #ba68c8)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {modoAberto ? "Processos Abertos" : "Processos Finalizados"}
              </Typography>

              <Chip
                label={modoAberto ? "Abertos" : "Finalizados"}
                color={modoAberto ? "primary" : "secondary"}
                variant="outlined"
                sx={{
                  fontWeight: "bold",
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <ModeSwitchButton
                variant="contained"
                onClick={handleToggleMode}
                startIcon={<SwapHorizIcon />}
                color={modoAberto ? "primary" : "secondary"}
                sx={{
                  borderRadius: "20px",
                  px: 3,
                  py: 1,
                  fontWeight: "bold",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                  background: modoAberto
                    ? "linear-gradient(45deg, #1976d2, #2196f3)"
                    : "linear-gradient(45deg, #9c27b0, #ba68c8)",
                }}
                disabled={isTransitioning}
              >
                {isTransitioning
                  ? "Carregando..."
                  : `Ver ${modoAberto ? "Finalizados" : "Abertos"}`}
              </ModeSwitchButton>

              <TextField
                variant="outlined"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => {
                  const newSearchTerm = e.target.value;

                  setSearchTerm(newSearchTerm);

                  saveLastSearch({
                    searchTerm: newSearchTerm,
                  });
                }}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                  sx: {
                    borderRadius: "20px",
                    backgroundColor: "background.paper",
                  },
                }}
                sx={{
                  minWidth: "300px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                  },
                }}
              />

              <Tooltip title="Ordenar por nome">
                <IconButton
                  onClick={() => handleSort("name")}
                  color={sortConfig.key === "name" ? "primary" : "default"}
                >
                  <SortIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Ordenar por quantidade">
                <IconButton
                  onClick={() => handleSort("count")}
                  color={sortConfig.key === "count" ? "primary" : "default"}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </motion.div>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          {assignmentFilterOptions.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              onClick={() => handleAssignmentFilterChange(option.value)}
              color={
                assignmentFilter === option.value
                  ? modoAberto
                    ? "primary"
                    : "secondary"
                  : "default"
              }
              variant={
                assignmentFilter === option.value ? "filled" : "outlined"
              }
            />
          ))}
        </Box>

        {/* Conteúdo */}
        <TransitionContainer>
          <AnimatePresence mode="wait">
            {isTransitioning ? (
              <motion.div
                key="loading-transition"
                initial={{
                  opacity: 0.5,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -20,
                }}
                transition={{
                  duration: 0.3,
                }}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "300px",
                }}
              >
                <motion.div
                  animate={{
                    rotate: 360,
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    rotate: {
                      duration: 1,
                      repeat: Infinity,
                      ease: "linear",
                    },
                    scale: {
                      duration: 1.5,
                      repeat: Infinity,
                      repeatType: "reverse",
                    },
                  }}
                >
                  <SwapHorizIcon
                    color="primary"
                    sx={{
                      fontSize: 60,
                    }}
                  />
                </motion.div>
              </motion.div>
            ) : loading ? (
              <motion.div
                key="loading"
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -20,
                }}
                transition={{
                  duration: 0.3,
                }}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "300px",
                }}
              >
                <motion.div
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                >
                  <CircularProgress
                    size={60}
                    thickness={4}
                    color={modoAberto ? "primary" : "secondary"}
                  />
                </motion.div>
              </motion.div>
            ) : error ? (
              <motion.div
                key="error"
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -20,
                }}
                transition={{
                  duration: 0.3,
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: "16px",
                    p: 4,
                    textAlign: "center",
                    background: "linear-gradient(45deg, #f5f5f5, #ffffff)",
                  }}
                >
                  <Typography color="error" variant="h6" sx={{ mb: 2 }}>
                    Ocorreu um erro ao carregar os processos
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 3 }}>
                    {error}
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={fetchProcesses}
                    startIcon={<RefreshIcon />}
                    color={modoAberto ? "primary" : "secondary"}
                    sx={{
                      borderRadius: "20px",
                      px: 4,
                      py: 1.5,
                      fontWeight: "bold",
                    }}
                  >
                    Tentar Novamente
                  </Button>
                </Paper>
              </motion.div>
            ) : schools.length === 0 ? (
              <motion.div
                key="empty"
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -20,
                }}
                transition={{
                  duration: 0.3,
                }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: "16px",
                    overflow: "hidden",
                    background: "linear-gradient(45deg, #fafafa, #ffffff)",
                  }}
                >
                  <EmptyProcessState
                    onRetry={fetchProcesses}
                    mode={modoAberto ? "open" : "closed"}
                  />
                </Paper>
              </motion.div>
            ) : (
              <motion.div
                key="content"
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  y: -20,
                }}
                transition={{
                  duration: 0.3,
                }}
              >
                {filteredSchools.length > 0 ? (
                  <Paper
                    ref={containerRef}
                    elevation={3}
                    sx={{
                      borderRadius: "16px",
                      overflow: "hidden",
                      background: "linear-gradient(45deg, #fafafa, #ffffff)",
                    }}
                  >
                    <List disablePadding>
                      {filteredSchools.map((school, index) => (
                        <motion.div
                          key={`school-${index}`}
                          layout
                          transition={{
                            duration: 0.3,
                          }}
                        >
                          <SchoolItem
                            school={school}
                            expanded={expandedSchool === school.name}
                            onClick={() => handleExpandClick(school.name)}
                            mode={modoAberto ? "open" : "closed"}
                          />

                          <Collapse
                            in={expandedSchool === school.name}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Divider
                              sx={{
                                mx: 2,
                              }}
                            />

                            <Box
                              sx={{
                                pl: 4,
                                pr: 2,
                                pt: 1,
                                pb: 2,
                              }}
                            >
                              {school.modalities.map((modality, idx) => (
                                <motion.div
                                  key={`modality-${idx}`}
                                  layout
                                  transition={{
                                    duration: 0.2,
                                  }}
                                >
                                  <ModalityItem
                                    modality={modality}
                                    school={school}
                                    expanded={
                                      expandedModality[school.name] ===
                                      modality.name
                                    }
                                    onClick={() =>
                                      handleModalityClick(
                                        school.name,
                                        modality.name,
                                      )
                                    }
                                  />

                                  <Collapse
                                    in={
                                      expandedModality[school.name] ===
                                      modality.name
                                    }
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <List
                                      disablePadding
                                      sx={{
                                        pl: 4,
                                      }}
                                    >
                                      <AnimatePresence>
                                        {modality.processes.map(
                                          (process, idx2) => (
                                            <ProcessItem
                                              key={`process-${process.process_id}`}
                                              process={process}
                                              school={school}
                                              onClick={() =>
                                                handleProcessClick(
                                                  school,
                                                  process,
                                                )
                                              }
                                            />
                                          ),
                                        )}
                                      </AnimatePresence>
                                    </List>
                                  </Collapse>
                                </motion.div>
                              ))}
                            </Box>
                          </Collapse>
                        </motion.div>
                      ))}
                    </List>
                  </Paper>
                ) : (
                  <motion.div
                    layout
                    transition={{
                      duration: 0.3,
                    }}
                  >
                    <Paper
                      elevation={3}
                      sx={{
                        borderRadius: "16px",
                        p: 4,
                        textAlign: "center",
                        background: "linear-gradient(45deg, #f5f5f5, #ffffff)",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 2,
                        }}
                      >
                        Nenhum resultado encontrado
                      </Typography>

                      <Typography
                        variant="body1"
                        sx={{
                          mb: 3,
                        }}
                      >
                        {searchTerm.trim()
                          ? `Sua busca por "${searchTerm}" não retornou nenhum resultado.`
                          : "Nenhuma escola corresponde ao filtro selecionado."}
                      </Typography>

                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSearchTerm("");

                          saveLastSearch({
                            searchTerm: "",
                          });

                          handleAssignmentFilterChange("all");
                        }}
                        color={modoAberto ? "primary" : "secondary"}
                        sx={{
                          borderRadius: "20px",
                          px: 4,
                          py: 1.5,
                          fontWeight: "bold",
                        }}
                      >
                        Limpar Filtros
                      </Button>
                    </Paper>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </TransitionContainer>
      </Container>
    </TransicaoPagina>
  );
}
